import { useTranslation as useI18nTranslation } from "react-i18next";

/**
 * Custom hook for translations with additional utilities
 * Extends the react-i18next useTranslation hook with app-specific functionality
 *
 * @param {string} [namespace] - Optional namespace for translations
 * @returns {object} Translation utilities
 */
export function useTranslation(namespace) {
  const translation = useI18nTranslation(namespace);

  return {
    ...translation,

    /**
     * Translate with fallback text
     * @param {string} key - Translation key
     * @param {string} fallback - Fallback text if translation is missing
     * @param {object} options - Translation options (interpolation, etc.)
     * @returns {string} Translated text or fallback
     */
    tf: (key, fallback, options = {}) => {
      const translated = translation.t(key, {
        ...options,
        defaultValue: fallback,
      });
      return translated !== key ? translated : fallback;
    },

    /**
     * Check if a translation key exists
     * @param {string} key - Translation key to check
     * @returns {boolean} True if key exists
     */
    exists: (key) => translation.i18n.exists(key),

    /**
     * Get current language data
     * @returns {object} Current language information
     */
    getCurrentLanguage: () => {
      const lang = translation.i18n.language || "en";
      return {
        code: lang,
        isRTL: ["ar", "he", "fa"].includes(lang), // Add RTL languages if needed
        direction: ["ar", "he", "fa"].includes(lang) ? "rtl" : "ltr",
      };
    },
  };
}

/**
 * Hook specifically for form translations
 * Provides common form-related translation keys
 *
 * @returns {object} Form translation utilities
 */
export function useFormTranslation() {
  const { t, tf } = useTranslation();

  return {
    t,
    tf,
    // Common form labels
    labels: {
      username: t("auth.username"),
      password: t("auth.password"),
      email: t("common.email", "Email"),
      name: t("common.name", "Name"),
      description: t("common.description", "Description"),
    },
    // Common form actions
    actions: {
      save: t("common.save"),
      cancel: t("common.cancel"),
      delete: t("common.delete"),
      edit: t("common.edit"),
      submit: t("common.submit"),
      reset: t("common.reset"),
    },
    // Common form messages
    messages: {
      required: t("validation.required", "This field is required"),
      invalid: t("validation.invalid", "Invalid value"),
      tooShort: t("validation.tooShort", "Too short"),
      tooLong: t("validation.tooLong", "Too long"),
    },
  };
}

/**
 * Hook for navigation translations
 * Provides navigation-related translation keys
 *
 * @returns {object} Navigation translation utilities
 */
export function useNavigationTranslation() {
  const { t } = useTranslation();

  return {
    t,
    nav: {
      home: t("navigation.home"),
      cards: t("navigation.cards"),
      gameBoard: t("navigation.gameBoard"),
      lobby: t("navigation.lobby"),
      profile: t("navigation.profile"),
      settings: t("navigation.settings"),
    },
  };
}
